import React from 'react';
import { Box, Button, createStyles, Dialog, DialogActions, DialogContent, Grid, makeStyles, ModalProps, Theme, Typography } from "@material-ui/core";
import { Form } from 'react-final-form';
import { showNotification } from '../../App';
import { client } from '../..';
import gql from 'graphql-tag';

interface BulkMatterForecastDialogProps extends Pick<ModalProps, 'open' | 'onClose'>, BulkUpdateMatterForecastInput{
    total?: number;
}

interface ForecastParametersFormValues {
    startDate: Date | null;
    endDate: Date | null;
    duration?: string;
    feesToBeBilled: number | null;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {},
        textField: {
            width: '100%',
        },
        paper: {
            padding: theme.spacing(2),
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
        },
        actionsWrapper: {
            display: 'flex',
            flexFlow: 'row wrap',
            justifyContent: 'center',
            alignItems: 'center',
            flex: 1,
            gap: '30px',
            padding: '20px 0px',
        }
    })
);

export function BulkMatterForecastDialog(props: BulkMatterForecastDialogProps) {
    
    const classes = useStyles();
                
    const { 
        filter,
        isIncludeCompleted,
        searchCriteriasJson,
        exclusionList,
        total,
        ...rest
    } = props;
    
    console.log('BulkMatterForecastDialog props:', { filter, isIncludeCompleted, searchCriteriasJson, exclusionList });
    
    const initialValues: ForecastParametersFormValues = {
        startDate: null,
        endDate: null,
        duration: '',
        feesToBeBilled: null,
    };
    
    const onSubmit = (values: ForecastParametersFormValues) => {
        
        client
            .mutate<BulkUpdateMatterForecastData, BulkUpdateMatterForecastVariables>({
                mutation: BULK_UPDATE_MATTER_FORECAST_MUTATION,
                variables: {
                    input: {
                        exclusionList: exclusionList,
                        filter: filter,
                        isIncludeCompleted: isIncludeCompleted,
                        searchCriteriasJson: searchCriteriasJson,
                    }
                }
            })
            // tslint:disable-next-line: no-any
            .then((results) => {
                if (results.data?.matter.matterForecast.bulkMatterAutomation.status && !results.data.matter.matterForecast.bulkMatterAutomation.error) {
                    showNotification(null, 'Matter Forecast Updated Successfully', 'info');
                    props.onClose?.({}, 'backdropClick');
                } else {
                    showNotification('Failed to Update Matter Forecast', results.data?.matter.matterForecast.bulkMatterAutomation.error ?? '', 'error');
                }
            })
            // tslint:disable-next-line:no-any
            .catch((reason: any) => {
                showNotification('Failed to Update Matter Forecast', reason, 'error');
            });
    };
    
    const selectedTotal = (total || 0) - (exclusionList ? exclusionList.split(',').length : 0);
    
    return (
        <Dialog 
            {...rest}
            fullWidth={true}
            maxWidth="sm"
        >
            <Form<ForecastParametersFormValues>
                onSubmit={onSubmit}
                initialValues={initialValues}
                initialValuesEqual={() => true}
                // keepDirtyOnReinitialize={true}
                subscription={{ submitting: true, pristine: true, values: true }}
                render={({ handleSubmit, form, submitting, pristine, values}) => (
                    <form 
                        onSubmit={handleSubmit}
                        className={classes.root}
                    >
                        <DialogContent>
                            <Grid
                                container={true}
                                spacing={2}
                            >
                                <Grid
                                    item={true}
                                    xs={12}
                                >
                                <Box
                                    textAlign="center"
                                    display="flex"
                                    flexDirection="column"
                                    alignItems="center"
                                    gridGap="20px"
                                >
                                    <Typography
                                        variant="h5"
                                        gutterBottom={true}
                                    >
                                        Bulk Matter Forecast
                                    </Typography>          
                                    <Typography
                                        gutterBottom={true}
                                    >
                                        {selectedTotal} matters selected
                                    </Typography>    
                                    <Typography
                                        gutterBottom={true}
                                    >
                                        Processing bulk matter forecast updates may take several minutes depending on the number of matters selected. You will be notified once the process is complete.
                                    </Typography>    
                                </Box>
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogActions>
                            <div
                                className={classes.actionsWrapper}
                            >
                                 <Button
                                    type="submit"
                                    disabled={submitting}
                                    variant="outlined"
                                >
                                    Create Forecasts
                                </Button>
                                <Button
                                    type="button"
                                    variant="outlined"
                                    onClick={() => props.onClose?.({}, 'backdropClick')}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </DialogActions>
                    </form>
                )}
            />
        </Dialog>
    );
}

interface BulkUpdateMatterForecastInput {
    filter?: string;
    isIncludeCompleted?: boolean;
    searchCriteriasJson?: string;
    exclusionList?: string;
}

interface BulkUpdateMatterForecastVariables {
    input: BulkUpdateMatterForecastInput;
}

interface BulkUpdateMatterForecastData {
    matter: Matter;
}

interface Matter {
    matterForecast: MatterForecast;
}

interface MatterForecast {
    bulkMatterAutomation: BulkMatterAutomation;
}

interface BulkMatterAutomation {
    error:  null;
    status: boolean;
}

const BULK_UPDATE_MATTER_FORECAST_MUTATION = gql`
    mutation BulkUpdateMatterForecast($input: BulkMatterForecastAutomationInput  ) {
        matter {
            matterForecast {
                bulkMatterAutomation(input: $input) {
                    error
                    status
                }
            }
        }
    }
`;